import { useEffect, useState, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Pencil } from 'lucide-react';
import { invoiceApi, type Invoice, type StripeImportPreview } from '../../lib/api';
import { useCompany } from '../CompanyProvider';
import { useConfirm } from '../ConfirmProvider';
import { Button, Card, EmptyState, ErrorNote, PageHeader, PartyBadge, StatusBadge, euro, displayCustomer } from '../ui';

type Tab = 'invoice' | 'quote';

/**
 * Stripe-Rechnungsimport in zwei Schritten: erst Vorschau (schreibt nichts),
 * dann Übernahme. Die Kundenzuordnung passiert hier, weil der Stripe-Export als
 * Kunde nur die ID (cus_...) enthält — ohne Name und ohne E-Mail ist eine
 * automatische Zuordnung nicht möglich. Einmal zugeordnet, merkt Saldify sie
 * für spätere Importe.
 */
function StripeImportDialog({
  companyId,
  onClose,
  onDone,
}: {
  companyId: string;
  onClose: () => void;
  onDone: () => void;
}): ReactElement {
  const [content, setContent] = useState('');
  const [preview, setPreview] = useState<StripeImportPreview | null>(null);
  const [zuordnung, setZuordnung] = useState<Record<string, string>>({});
  const [taxRate, setTaxRate] = useState('0');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [ergebnis, setErgebnis] = useState<{ angelegt: number; uebersprungen: number; fehler: string[] } | null>(null);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(null);
    setBusy(true);
    try {
      const text = await file.text();
      setContent(text);
      const p = await invoiceApi.importStripePreview(companyId, text);
      setPreview(p);
      // Bereits bekannte Zuordnungen vorbelegen.
      const vor: Record<string, string> = {};
      for (const g of p.kundenGruppen) if (g.customerId) vor[g.stripeCustomerId] = g.customerId;
      setZuordnung(vor);
    } catch (e2) {
      setErr(e2);
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    setErr(null);
    setBusy(true);
    try {
      setErgebnis(await invoiceApi.importStripeApply(companyId, content, zuordnung, Number(taxRate) || 0));
    } catch (e2) {
      setErr(e2);
    } finally {
      setBusy(false);
    }
  }

  const neu = preview?.neu ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-2xl bg-white p-6 dark:bg-gray-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-1 text-lg font-bold text-gray-900 dark:text-gray-100">Rechnungen aus Stripe importieren</h2>
        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          Im Stripe-Dashboard unter <em>Rechnungen → Exportieren</em> eine CSV herunterladen und hier hochladen.
          Bereits importierte Rechnungen werden erkannt und übersprungen.
        </p>

        {err ? <div className="mb-3"><ErrorNote error={err} /></div> : null}

        {ergebnis ? (
          <div className="space-y-3">
            <div className="rounded-xl bg-emerald-50 p-4 text-sm text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300">
              <p className="font-semibold">{ergebnis.angelegt} Rechnung(en) importiert.</p>
              {ergebnis.uebersprungen > 0 && (
                <p className="mt-1 text-xs">{ergebnis.uebersprungen} bereits vorhanden – übersprungen.</p>
              )}
            </div>
            {ergebnis.fehler.length > 0 && (
              <div className="rounded-xl bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
                <p className="font-medium">Nicht importiert:</p>
                <ul className="mt-1 list-inside list-disc">{ergebnis.fehler.slice(0, 8).map((f, i) => <li key={i}>{f}</li>)}</ul>
              </div>
            )}
            <div className="flex justify-end"><Button onClick={onDone}>Fertig</Button></div>
          </div>
        ) : !preview ? (
          <div>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={onFile}
              disabled={busy}
              className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-blue-600 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-blue-700 dark:text-gray-300"
            />
            {busy && <p className="mt-2 text-xs text-gray-500">Datei wird geprüft …</p>}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border border-gray-200 p-3 text-sm dark:border-gray-700">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Gefunden</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{preview.rechnungen.length} Rechnungen</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Davon neu</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{neu}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Summe (neu)</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {euro(preview.rechnungen.filter((r) => !r.bereitsImportiert).reduce((s, r) => s + r.grossCents, 0))}
                </span>
              </div>
            </div>

            {preview.fremdwaehrung.length > 0 && (
              <div className="rounded-xl bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
                Der Export enthält Fremdwährung ({preview.fremdwaehrung.join(', ')}). Diese Rechnungen können noch
                nicht importiert werden.
              </div>
            )}

            {/* Kundenzuordnung — der Kern des Dialogs. */}
            <div>
              <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">Kunden zuordnen</p>
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                Der Stripe-Export enthält nur die Kunden-ID. Ordne sie deinen Kontakten zu – ohne Zuordnung wird die
                Rechnung ohne Empfänger importiert.
              </p>
              <div className="space-y-2">
                {preview.kundenGruppen.map((g) => (
                  <div key={g.stripeCustomerId} className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 p-2 dark:border-gray-700">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-mono text-xs text-gray-700 dark:text-gray-300">{g.stripeCustomerId}</span>
                      <span className="block text-xs text-gray-400">{g.anzahl} Rechnungen · {euro(g.summeCents)}</span>
                    </span>
                    <select
                      value={zuordnung[g.stripeCustomerId] ?? ''}
                      onChange={(e) => setZuordnung((p) => ({ ...p, [g.stripeCustomerId]: e.target.value }))}
                      className="min-w-[12rem] rounded-lg border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                    >
                      <option value="">— ohne Kunde —</option>
                      {preview.kunden.map((k) => (
                        <option key={k.id} value={k.id}>{k.label}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Umsatzsteuer</span>
              <select
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
              >
                <option value="0">0 % – wie im Stripe-Export ausgewiesen</option>
                <option value="7">7 %</option>
                <option value="19">19 %</option>
              </select>
              <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                Der Export weist die Steuer bereits aus; die Auswahl gilt für die Positionszeile der importierten Rechnung.
              </span>
            </label>

            {/* Die einzelnen Rechnungen, damit klar ist, was ankommt. */}
            <details className="rounded-xl border border-gray-200 p-3 dark:border-gray-700">
              <summary className="cursor-pointer text-sm font-medium text-gray-900 dark:text-gray-100">
                Rechnungen im Detail
              </summary>
              <div className="mt-2 max-h-56 overflow-auto">
                {preview.rechnungen.map((r) => (
                  <div key={r.stripeId} className={`flex items-baseline justify-between gap-2 py-0.5 text-xs ${r.bereitsImportiert ? 'text-gray-400 line-through' : 'text-gray-700 dark:text-gray-300'}`}>
                    <span className="truncate">{r.number ?? r.stripeId}</span>
                    <span className="shrink-0 text-gray-400">{r.date}</span>
                    <span className="shrink-0 tabular-nums">{euro(r.grossCents)}</span>
                  </div>
                ))}
              </div>
            </details>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>Abbrechen</Button>
              <Button onClick={apply} busy={busy} disabled={busy || neu === 0 || preview.fremdwaehrung.length > 0}>
                {neu === 0 ? 'Nichts zu importieren' : `${neu} Rechnung(en) importieren`}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Invoices(): ReactElement {
  const { active } = useCompany();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('invoice');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [importOpen, setImportOpen] = useState(false);
  // Jahrfilter: '' = alle. Bei Mandanten mit Alt-Import reicht die Liste ueber
  // viele Jahre, da ist das laufende Jahr ohne Filter kaum auffindbar.
  const [year, setYear] = useState<string>('');

  function load() {
    if (!active) return;
    setLoading(true);
    invoiceApi
      .list(active.id, { type: tab })
      .then(setInvoices)
      .finally(() => setLoading(false));
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, tab]);

  async function del(id: string) {
    if (!active) return;
    const ok = await confirm({ title: 'Entwurf löschen?', message: 'Dieser Entwurf wird dauerhaft entfernt.', confirmLabel: 'Löschen', danger: true });
    if (!ok) return;
    setError(null);
    try {
      await invoiceApi.remove(active.id, id);
      setInvoices((prev) => prev.filter((i) => i.id !== id));
      load();
    } catch (e) {
      setError(e);
    }
  }

  const isQuote = tab === 'quote';
  // Auswahl nur aus Jahren, die wirklich vorkommen — sonst stehen leere
  // Optionen in der Liste. Entwuerfe ohne Datum bleiben immer sichtbar.
  const years = [...new Set(
    invoices.map((i) => (i.issueDate ? new Date(i.issueDate).getFullYear() : null)).filter((y): y is number => y !== null),
  )].sort((a, b) => b - a);
  const shown = year
    ? invoices.filter((i) => i.issueDate && String(new Date(i.issueDate).getFullYear()) === year)
    : invoices;
  const newTo = isQuote ? '/app/rechnungen/neu?typ=angebot' : '/app/rechnungen/neu';
  const newLabel = isQuote ? '+ Neues Angebot' : '+ Neue Rechnung';

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Rechnungen & Angebote"
        subtitle="Erstelle und verwalte deine Rechnungen und Angebote."
        action={
          <div className="flex gap-2">
            {/* Import nur bei Rechnungen anbieten — Angebote gibt es bei Stripe nicht. */}
            {!isQuote && <Button variant="secondary" onClick={() => setImportOpen(true)}>Stripe-Import</Button>}
            <Link to={newTo}><Button>{newLabel}</Button></Link>
          </div>
        }
      />

      {importOpen && active && (
        <StripeImportDialog
          companyId={active.id}
          onClose={() => setImportOpen(false)}
          onDone={() => { setImportOpen(false); load(); }}
        />
      )}

      {/* Tabs: Rechnungen | Angebote */}
      <div className="mb-4 flex gap-1 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
        {([['invoice', 'Rechnungen'], ['quote', 'Angebote']] as [Tab, string][]).map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium transition ${
              tab === k ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-900 dark:text-gray-100' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {/* Jahrfilter — nur zeigen, wenn es ueberhaupt mehrere Jahre gibt. */}
      {years.length > 1 && (
        <div className="mb-4 flex items-center gap-2">
          <select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="">Alle Jahre</option>
            {years.map((y) => <option key={y} value={String(y)}>{y}</option>)}
          </select>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {shown.length} {isQuote ? (shown.length === 1 ? 'Angebot' : 'Angebote') : (shown.length === 1 ? 'Rechnung' : 'Rechnungen')}
          </span>
        </div>
      )}

      {error ? <div className="mb-4"><ErrorNote error={error} /></div> : null}

      {loading ? (
        <p className="text-gray-500">Lädt …</p>
      ) : shown.length === 0 && year ? (
        <EmptyState
          title={`Nichts in ${year}`}
          hint={isQuote ? 'In diesem Jahr gibt es keine Angebote.' : 'In diesem Jahr gibt es keine Rechnungen.'}
          action={<Button variant="secondary" onClick={() => setYear('')}>Alle Jahre zeigen</Button>}
        />
      ) : invoices.length === 0 ? (
        <EmptyState
          title={isQuote ? 'Noch keine Angebote' : 'Noch keine Rechnungen'}
          hint={isQuote ? 'Erstelle dein erstes Angebot – später per Klick in eine Rechnung umwandelbar.' : 'Erstelle deine erste Rechnung in wenigen Sekunden.'}
          action={<Link to={newTo}><Button>{newLabel}</Button></Link>}
        />
      ) : (
        <div className="space-y-2">
          {shown.map((inv) => {
            const empfaenger = inv.customer ? displayCustomer(inv.customer) : inv.recipientName ?? 'Kein Empfänger';
            // Ueberfaellig: faellig in der Vergangenheit und noch nicht bezahlt.
            const ueberfaellig =
              !!inv.dueDate && inv.status !== 'paid' && inv.status !== 'cancelled' &&
              new Date(inv.dueDate) < new Date(new Date().toDateString());
            return (
              <Card key={inv.id} className="flex items-center justify-between !p-4">
                <Link
                  to={`/app/rechnungen/${inv.id}`}
                  className="-m-1 flex min-w-0 flex-1 items-center gap-3 rounded-lg p-1 transition hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <PartyBadge name={empfaenger} />
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2 font-medium text-gray-900 dark:text-gray-100">
                      <span className="truncate">{empfaenger}</span>
                      <StatusBadge status={inv.status} />
                      {ueberfaellig && (
                        <span className="shrink-0 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
                          überfällig
                        </span>
                      )}
                      {inv.convertedToId && (
                        <span className="shrink-0 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">umgewandelt</span>
                      )}
                    </div>
                    <div className="truncate text-sm text-gray-500 dark:text-gray-400">
                      {inv.issueDate ? new Date(inv.issueDate).toLocaleDateString('de-DE') : 'ohne Datum'}
                      {` · Nr. ${inv.number ?? 'Entwurf'}`}
                      {inv.dueDate && inv.status !== 'paid'
                        ? ` · fällig ${new Date(inv.dueDate).toLocaleDateString('de-DE')}`
                        : ''}
                    </div>
                  </div>
                </Link>
                <div className="flex shrink-0 items-center gap-2 pl-2 sm:pl-3">
                  <span className="whitespace-nowrap font-semibold text-gray-900 dark:text-gray-100">{euro(inv.grossCents)}</span>
                  {inv.status === 'draft' && (
                    <button
                      type="button"
                      onClick={() => navigate(`/app/rechnungen/${inv.id}/bearbeiten`)}
                      aria-label="Entwurf bearbeiten"
                      className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-gray-700"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                  {inv.status === 'draft' && (
                    <button
                      type="button"
                      onClick={() => del(inv.id)}
                      aria-label="Entwurf löschen"
                      className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition hover:bg-red-50 hover:text-red-600 dark:hover:bg-gray-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
